import { UsersTexts } from './UsersTexts';
import { PageService } from '../../services/PageService.svelte';
import { UserService } from '../../services/UserService.svelte';
import { TeamIds } from '../../api/types/users.types';
import type { DeletionPermission } from '../../components/actions/actions.types';
import type { TeamBody, UserBody } from '../../api/types/users.types';

/**
 * What the logged-in user may do in the Users module (accounts, teams, the own account) — the one place every module
 * asks (`UsersRights.canOpenUser()`). It mirrors the API (users and teams: ADMIN, a root account and the team ROOT only
 * by a root, the own account: USER); the API stays the guard. A way to a page is left out without the right, a
 * forbidden action on a record stays visible, disabled, with the reason.
 */
class UsersRightsClass {
    // - the own account

    public canUseAccount(): boolean {
        return PageService.hasAccess('user');
    }

    // - accounts

    public canCreateUser(): boolean {
        return PageService.hasAccess('admin');
    }

    /** A person becomes a user — admins only, and only with an address (the person's details say so). */
    public canCreateUserForPerson(): boolean {
        return PageService.hasAccess('admin');
    }

    /** An admin may change every account but a root's; the API says it per account (`is_editable`). */
    public canEditUser(user: { is_editable?: boolean } | null | undefined): boolean {
        return PageService.hasAccess('admin') && user?.is_editable !== false;
    }

    /** Without any organization to read, the module and with it the own contacts do not exist. */
    public canSeeOwnContacts(): boolean {
        return PageService.canAccess('organizations');
    }

    public canListUsers(): boolean {
        return PageService.canAccess('users');
    }

    public canOpenUser(): boolean {
        return PageService.canAccess('admin_user');
    }

    public canSeeUserCard(): boolean {
        return PageService.hasAccess('admin');
    }

    public getUserDeletion(user: Partial<UserBody> | null | undefined): DeletionPermission {
        if (!user?.user_id || !PageService.hasAccess('admin')) {
            return { allowed: false, label: UsersTexts.UserDelete };
        }
        if (user.user_id === UserService.body.user?.user_id) {
            return { allowed: false, label: UsersTexts.UserNoSelfDelete };
        }

        return { allowed: true, label: UsersTexts.UserDelete };
    }

    // - teams

    /** ROOT is handed out (and taken) by a root only. */
    public canAssignTeam(teamId: number): boolean {
        return PageService.hasAccess('admin') && (teamId !== TeamIds.root || PageService.hasAccess('root'));
    }

    public canCreateTeam(): boolean {
        return PageService.hasAccess('admin');
    }

    public canEditTeam(team: Pick<TeamBody, 'team_id'> | null | undefined): boolean {
        return Boolean(team) && this.canAssignTeam(team?.team_id ?? 0);
    }

    public canListTeams(): boolean {
        return PageService.canAccess('teams');
    }

    public canOpenTeam(): boolean {
        return PageService.canAccess('admin_team');
    }

    public canSeeTeamCard(): boolean {
        return PageService.hasAccess('admin');
    }

    /** Built-in teams (USER, ADMIN, ROOT) never, a team with users not before they are moved (API: TEAM.protected, TEAM.used). */
    public getTeamDeletion(team: Partial<TeamBody> | null | undefined): DeletionPermission {
        if (!team?.team_id || !PageService.hasAccess('admin')) {
            return { allowed: false, label: UsersTexts.TeamDelete };
        }
        if (team.is_protected) {
            return { allowed: false, label: UsersTexts.TeamProtected };
        }
        if (team.is_used) {
            return { allowed: false, label: UsersTexts.TeamHasUsers };
        }

        return { allowed: true, label: UsersTexts.TeamDelete };
    }
}

export const UsersRights = new UsersRightsClass();
